/**
 * PDF Generation Utility
 * 
 * Provides reusable functions for generating Internal and Order PDFs
 * from measurement data using the measurementsTemplate.html template.
 */

import { buildPdfFilename } from './pdfFilename';
import { formatPatientNameForFilename } from './patientNameUtils';
import { getOrderOptionEntries, buildOrderOptionSource } from './orderOptions';

/**
 * Helper function to set input values in the PDF template
 */
const setInput = (doc, id, value) => {
    const el = doc.getElementById(id);
    if (el && value != null && value !== undefined && value !== '') {
        el.setAttribute('value', String(value));
    }
};

/**
 * Helper function to set textarea values in the PDF template
 */
const setTextarea = (doc, id, value) => {
    const el = doc.getElementById(id);
    if (el && value != null && value !== '') {
        el.textContent = String(value);
    }
};

/**
 * Format patient name as "Lastname, Firstname" for display
 */
const formatPatientNameForDisplay = (firstName, lastName, fallbackName = 'Patient') => {
    if (lastName && firstName) {
        return `${lastName}, ${firstName}`;
    }
    if (lastName) {
        return lastName;
    }
    if (firstName) {
        return firstName;
    }
    if (fallbackName && fallbackName !== 'Unknown' && fallbackName !== 'Patient') {
        return fallbackName;
    }
    return 'Patient';
};

/**
 * Safely normalize the date that should appear in the PDF as the measurement date.
 * The PDF should always use the measurement's original creation timestamp.
 */
const getMeasurementDateValue = (measurement) => {
    if (!measurement || !measurement.createdAt) return '';

    const date = measurement.createdAt instanceof Date
        ? measurement.createdAt
        : new Date(measurement.createdAt);

    if (Number.isNaN(date.getTime())) return '';

    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
};

/**
 * Populate patient information section
 */
const populatePatientInfo = (doc, measurement, t) => {
    const patientDisplayName = formatPatientNameForDisplay(
        measurement.patientFirstName,
        measurement.patientLastName,
        measurement.patientName
    );

    setInput(doc, 'patientName', patientDisplayName || '');
    setInput(doc, 'ssid', measurement.patientId || '');
    setInput(doc, 'address', measurement.patientAddress || '');
    setInput(doc, 'phoneNumber', measurement.patientPhoneNumber || '');
    setInput(doc, 'measurementDate', getMeasurementDateValue(measurement));
    const measuredBy = measurement.userId;
    const measuredByName = measuredBy && typeof measuredBy === 'object'
        ? `${measuredBy.firstName || ''} ${measuredBy.lastName || ''}`.trim()
        : '';
    setInput(doc, 'measuredBy', measuredByName || 'Unknown');

    // Measurement Type - translate the value
    if (measurement.measurementType) {
        const translatedMeasurementType = t(`measurements.legMeasuring.measurementType.${measurement.measurementType}`);
        setInput(doc, 'measurementType', translatedMeasurementType);
    }
};

/**
 * Populate prescription details section
 */
const populatePrescriptionDetails = (doc, measurement) => {
    setInput(doc, 'compressionClass', measurement.compressionClass);
    setInput(doc, 'lengthType', measurement.lengthType);
    setInput(doc, 'knitting', measurement.knitting);
    setInput(doc, 'prescribingDoctor', measurement.prescribingDoctor);
};

/**
 * Populate HM (horizontal measurements) section
 */
const populateHmMeasurements = (doc, measurement) => {
    const m = measurement.measurements || {};

    // Hip / waist
    setInput(doc, 'cT-hm', m.cT);
    setInput(doc, 'cH-hm', m.cH);

    // Left leg circumference
    setInput(doc, 'left-cG-hm', m.cGL);
    setInput(doc, 'left-cF-hm', m.cFL);
    setInput(doc, 'left-cE-hm', m.cEL);
    setInput(doc, 'left-cD-hm', m.cDL);
    setInput(doc, 'left-cC-hm', m.cCL);
    setInput(doc, 'left-cB1-hm', m.cB1L);
    setInput(doc, 'left-cB-hm', m.cBL);
    setInput(doc, 'left-cY-hm', m.cYL);
    setInput(doc, 'left-cA-hm', m.cAL);

    // Right leg circumference
    setInput(doc, 'right-cG-hm', m.cGR);
    setInput(doc, 'right-cF-hm', m.cFR);
    setInput(doc, 'right-cE-hm', m.cER);
    setInput(doc, 'right-cD-hm', m.cDR);
    setInput(doc, 'right-cC-hm', m.cCR);
    setInput(doc, 'right-cB1-hm', m.cB1R);
    setInput(doc, 'right-cB-hm', m.cBR);
    setInput(doc, 'right-cY-hm', m.cYR);
    setInput(doc, 'right-cA-hm', m.cAR);

    // Length measurements
    setInput(doc, 'lT-hm', m.lT);
    setInput(doc, 'lH-hm', m.lH);
    setInput(doc, 'lG-hm', m.lG);
    setInput(doc, 'lF-hm', m.lF);
    setInput(doc, 'lE-hm', m.lE);
    setInput(doc, 'lD-hm', m.lD);
    setInput(doc, 'lC-hm', m.lC);
    setInput(doc, 'lB1-hm', m.lB1);
    setInput(doc, 'lB-hm', m.lB);
    setInput(doc, 'lA-hm', m.lA);
    setInput(doc, 'lZ-hm', m.lZ);
};

/**
 * Populate ZM (vertical measurements) section and handle visibility
 */
const populateZmMeasurements = (doc, measurement) => {
    const m2 = measurement.measurements2 || {};
    const hasMeasurements2 = m2 && Object.keys(m2).length > 0;

    if (hasMeasurements2) {
        // Populate ZM measurements from measurements2
        // Hip / waist
        setInput(doc, 'cT-zm', m2.cT);
        setInput(doc, 'cH-zm', m2.cH);

        // Left leg circumference
        setInput(doc, 'left-cG-zm', m2.cGL);
        setInput(doc, 'left-cF-zm', m2.cFL);
        setInput(doc, 'left-cE-zm', m2.cEL);
        setInput(doc, 'left-cD-zm', m2.cDL);
        setInput(doc, 'left-cC-zm', m2.cCL);
        setInput(doc, 'left-cB1-zm', m2.cB1L);
        setInput(doc, 'left-cB-zm', m2.cBL);
        setInput(doc, 'left-cY-zm', m2.cYL);
        setInput(doc, 'left-cA-zm', m2.cAL);

        // Right leg circumference
        setInput(doc, 'right-cG-zm', m2.cGR);
        setInput(doc, 'right-cF-zm', m2.cFR);
        setInput(doc, 'right-cE-zm', m2.cER);
        setInput(doc, 'right-cD-zm', m2.cDR);
        setInput(doc, 'right-cC-zm', m2.cCR);
        setInput(doc, 'right-cB1-zm', m2.cB1R);
        setInput(doc, 'right-cB-zm', m2.cBR);
        setInput(doc, 'right-cY-zm', m2.cYR);
        setInput(doc, 'right-cA-zm', m2.cAR);

        // Right leg length measurements
        setInput(doc, 'lT-zm', m2.lT);
        setInput(doc, 'lH-zm', m2.lH);
        setInput(doc, 'lG-zm', m2.lG);
        setInput(doc, 'lF-zm', m2.lF);
        setInput(doc, 'lE-zm', m2.lE);
        setInput(doc, 'lD-zm', m2.lD);
        setInput(doc, 'lC-zm', m2.lC);
        setInput(doc, 'lB1-zm', m2.lB1);
        setInput(doc, 'lB-zm', m2.lB);
        setInput(doc, 'lA-zm', m2.lA);
        setInput(doc, 'lZ-zm', m2.lZ);
    } else {
        // Hide all ZM measurements if measurements2 doesn't exist or is empty
        const zmFields = [
            // Hip/Waist ZM fields
            'cT-zm', 'cH-zm',
            // Left leg ZM fields
            'left-cG-zm', 'left-cF-zm', 'left-cE-zm', 'left-cD-zm', 'left-cC-zm',
            'left-cB1-zm', 'left-cB-zm', 'left-cY-zm', 'left-cA-zm',
            // Right leg ZM fields
            'right-cG-zm', 'right-cF-zm', 'right-cE-zm', 'right-cD-zm', 'right-cC-zm',
            'right-cB1-zm', 'right-cB-zm', 'right-cY-zm', 'right-cA-zm',
            // Right leg length measurements
            'lT-zm', 'lH-zm', 'lG-zm', 'lF-zm', 'lE-zm', 'lD-zm', 'lC-zm',
            'lB1-zm', 'lB-zm', 'lA-zm', 'lZ-zm'
        ];

        zmFields.forEach(fieldId => {
            const input = doc.getElementById(fieldId);
            if (input) {
                // Hide the parent div that contains both the ZM label and input
                const parentDiv = input.closest('div');
                if (parentDiv && parentDiv.querySelector('.measure-type')) {
                    const measureType = parentDiv.querySelector('.measure-type');
                    // Hide if measure type is 'ZM' or 'Right' (for length measurements)
                    if (measureType && (measureType.textContent === 'ZM' || measureType.textContent === 'Right')) {
                        parentDiv.style.display = 'none';
                    }
                }
            }
        });

        // Hide "Left" labels for length measurements when there's only one column
        const leftLengthFields = ['lT-hm', 'lH-hm', 'lG-hm', 'lF-hm', 'lE-hm', 'lD-hm', 'lC-hm', 'lB1-hm', 'lB-hm', 'lA-hm', 'lZ-hm'];
        leftLengthFields.forEach(fieldId => {
            const input = doc.getElementById(fieldId);
            if (input) {
                const parentDiv = input.closest('div');
                if (parentDiv) {
                    const measureTypeSpan = parentDiv.querySelector('.measure-type');
                    if (measureTypeSpan && measureTypeSpan.textContent === 'Left') {
                        measureTypeSpan.style.display = 'none';
                    }
                }
            }
        });
    }

    // Hide 'HM' labels when measurementType is 'offTheShelf' (only one set of measurements)
    if (measurement.measurementType === 'offTheShelf') {
        doc.querySelectorAll('.measure-type').forEach(span => {
            if (span.textContent.trim() === 'HM') {
                span.style.display = 'none';
            }
        });
    }
};

/**
 * Fields to exclude from Options section (already shown in Vendor Product section)
 */
const EXCLUDED_OPTION_FIELDS = new Set([
    'vendorType',
    'vendortype',
    'size',
    'lengthSize',
    'lengthsize',
    'compressionClass',
    'compressionclass',
    'length',
    'lengthType',
    'lengthtype',
    'knitting',
    'knittingType',
    'knittingtype'
]);

/**
 * Shared helper to populate product options section
 * Used by both Internal and Order PDFs
 * 
 * @param {Document} doc - The PDF document
 * @param {Object} measurement - The measurement data
 * @param {Function} t - Translation function
 * @param {string} notesSource - Which notes field to use ('notes' or 'order')
 */
const populateProductOptionsShared = (doc, measurement, t, notesSource = 'notes') => {
    const sp = measurement.selectedProduct || {};
    const sel = measurement.selections || {};

    // Build order option source (same as Order.js)
    const orderOptionSource = buildOrderOptionSource({
        selections: sel,
        measurement,
        selectedProduct: sp,
    });

    // Vendor section - show vendor product details
    setInput(doc, 'vendorName', sp.vendorName || '');
    setInput(doc, 'modelName', sp.modelName || '');
    setInput(doc, 'compressionClass', orderOptionSource.compressionClass || '');
    setInput(doc, 'lengthType', orderOptionSource.lengthType || '');
    setInput(doc, 'vendorType', sel.vendortype || '');
    setInput(doc, 'size', sel.size || '');
    setInput(doc, 'lengthSize', sel.lengthsize || sel.lengthSize || sp.lengthsize || sp.lengthSize || '');

    // Dynamically render all non-empty options from measurement.selections
    // excluding fields already shown in the Vendor Product section
    const optionsSectionTitle = doc.querySelector('[data-i18n="pdfTemplate.sections.options"]');
    const optionsSection = optionsSectionTitle ? optionsSectionTitle.closest('.section') : null;
    const optionsRow = optionsSection ? optionsSection.querySelector('.form-row') : null;

    const optionEntries = getOrderOptionEntries(orderOptionSource)
        .filter(({ key }) => !EXCLUDED_OPTION_FIELDS.has(key));

    if (optionsSection && optionsRow) {
        optionsRow.innerHTML = '';

        optionEntries.forEach(({ key, value, label: fallbackLabel }) => {
            const formGroup = doc.createElement('div');
            formGroup.className = 'form-group';

            const label = doc.createElement('label');
            const inputId = `option-${key}`;
            label.setAttribute('for', inputId);
            const translated = t(`pdfTemplate.labels.${key}`);
            label.textContent = translated && translated !== `pdfTemplate.labels.${key}`
                ? translated
                : fallbackLabel;

            const input = doc.createElement('input');
            input.type = 'text';
            input.id = inputId;
            input.setAttribute('value', String(value));

            formGroup.appendChild(label);
            formGroup.appendChild(input);
            optionsRow.appendChild(formGroup);
        });

        if (optionEntries.length === 0) {
            optionsSection.style.display = 'none';
        }
    }

    // Populate notes from appropriate source
    const notesValue = notesSource === 'order'
        ? (measurement.order?.orderNote || '')
        : (measurement.notes || '');
    setTextarea(doc, 'notes', notesValue);
};

/**
 * Populate product options section for Internal PDF
 * Uses dynamic options and excludes fields already in Vendor Product section
 */
const populateProductOptions = (doc, measurement, t) => {
    populateProductOptionsShared(doc, measurement, t, 'notes');
};

/**
 * Dynamically populate product options for Order PDF (matches Order.js logic)
 * Renders all non-empty options from measurement.selections
 */
const populateDynamicProductOptions = (doc, measurement, t) => {
    populateProductOptionsShared(doc, measurement, t, 'order');
};

/**
 * Populate order-specific fields (for Order PDF)
 */
const populateOrderFields = (doc, measurement, t) => {
    if (!measurement.order) return;

    const orderInfo = measurement.order;

    // Auto-generate reference number if not provided
    let finalReferenceNumber = orderInfo.referenceNumber;
    if (!finalReferenceNumber || finalReferenceNumber.trim() === '') {
        const patientDisplayName = formatPatientNameForFilename(
            measurement.patientFirstName,
            measurement.patientLastName
        );
        const measurementDate = measurement.createdAt ? new Date(measurement.createdAt) : new Date();
        const year = measurementDate.getFullYear().toString().slice(-2); // YY
        const month = (measurementDate.getMonth() + 1).toString().padStart(2, '0'); // MM
        const day = measurementDate.getDate().toString().padStart(2, '0'); // DD
        finalReferenceNumber = `${patientDisplayName} ${year}${month}${day}`;
    }

    setInput(doc, 'referenceNumber', finalReferenceNumber);
    setInput(doc, 'numberOfItems', orderInfo.numberOfItems || '1');

    // Translate and set unit
    if (orderInfo.unit) {
        const translatedUnit = t(`measurements.stockingSelection.order.${orderInfo.unit}`);
        setInput(doc, 'unit', translatedUnit);
    }

    setInput(doc, 'account', orderInfo.accountNumber || '');

    // Set radio buttons for order type (order vs quote)
    const orderTypeRadios = doc.querySelectorAll('input[name="orderType"]');
    orderTypeRadios.forEach(radio => {
        if (radio.value === (orderInfo.orderType || 'order')) {
            radio.setAttribute('checked', 'checked');
        } else {
            radio.removeAttribute('checked');
        }
    });

    // Ordered date
    if (measurement.orderedDate || orderInfo.orderedDate) {
        const orderedDate = new Date(measurement.orderedDate || orderInfo.orderedDate);
        const day = String(orderedDate.getDate()).padStart(2, '0');
        const month = orderedDate.toLocaleString('en-US', { month: 'short' });
        const year = orderedDate.getFullYear();
        setInput(doc, 'orderDate', `${day}-${month}-${year}`);
    }

    // Order note
    if (orderInfo.orderNote) {
        setTextarea(doc, 'orderNote', orderInfo.orderNote);
    }
};

/**
 * Generate Internal PDF (patient info, measurements, and order information)
 * 
 * @param {Object} measurement - The measurement data
 * @param {Function} t - Translation function from i18n
 * @returns {Promise<void>}
 */
export const generateInternalPdf = async (measurement, t) => {
    try {
        const response = await fetch('/measurementsTemplate.html');
        if (!response.ok) throw new Error('Template not found');
        const htmlText = await response.text();

        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlText, 'text/html');

        // Translate all elements with data-i18n attributes
        doc.querySelectorAll('[data-i18n]').forEach(el => {
            const key = el.getAttribute('data-i18n');
            el.textContent = t(key);
        });

        populateOrderFields(doc, measurement, t);

        // Prepend patient name to the h1 title
        const titleEl = doc.querySelector('h1[data-i18n="pdfTemplate.title"]');
        const patientDisplayName = formatPatientNameForDisplay(
            measurement.patientFirstName,
            measurement.patientLastName,
            measurement.patientName
        );
        if (titleEl && patientDisplayName) {
            titleEl.textContent = `${patientDisplayName} - ${titleEl.textContent}`;
        }

        // Populate all sections
        populatePatientInfo(doc, measurement, t);
        populatePrescriptionDetails(doc, measurement);
        populateHmMeasurements(doc, measurement);
        populateZmMeasurements(doc, measurement);
        populateProductOptions(doc, measurement, t);

        // Inject Supplier Order Note to the right of Additional Notes (if present)
        const orderNote = measurement.order?.orderNote;
        if (orderNote) {
            const notesTextarea = doc.getElementById('notes');
            if (notesTextarea) {
                const notesSection = notesTextarea.closest('.section');
                if (notesSection) {
                    const gridContainer = notesSection.parentElement;
                    if (gridContainer) {
                        // Expand grid from 2 to 3 columns
                        gridContainer.style.gridTemplateColumns = '1fr 1fr 1fr';

                        const supplierNoteSection = doc.createElement('div');
                        supplierNoteSection.className = 'section';
                        supplierNoteSection.style.marginBottom = '0';

                        const sectionTitle = doc.createElement('div');
                        sectionTitle.className = 'section-title';
                        sectionTitle.textContent = t('pdfTemplate.sections.supplierOrderNote');

                        const container = doc.createElement('div');
                        container.className = 'measurements-container';

                        const formGroup = doc.createElement('div');
                        formGroup.className = 'form-group';

                        const textarea = doc.createElement('textarea');
                        textarea.id = 'supplierOrderNote';
                        textarea.rows = 18;
                        textarea.style.minHeight = '150px';
                        textarea.style.resize = 'vertical';
                        textarea.textContent = orderNote;

                        formGroup.appendChild(textarea);
                        container.appendChild(formGroup);
                        supplierNoteSection.appendChild(sectionTitle);
                        supplierNoteSection.appendChild(container);
                        gridContainer.appendChild(supplierNoteSection);
                    }
                }
            }
        }

        // Hide the print button (not needed when auto-printing)
        const printBtn = doc.querySelector('.print-button');
        if (printBtn) printBtn.style.display = 'none';

        // Auto-trigger print dialog on load
        const script = doc.createElement('script');
        script.textContent = `window.addEventListener('load', function() { setTimeout(function() { window.print(); }, 400); });`;
        doc.body.appendChild(script);

        // Set document title
        doc.title = buildPdfFilename(
            formatPatientNameForFilename(measurement.patientFirstName, measurement.patientLastName),
            measurement.createdAt
        );

        // Serialize and create blob
        const serializer = new XMLSerializer();
        const populatedHtml = '<!DOCTYPE html>\n' + serializer.serializeToString(doc.documentElement);

        const blob = new Blob([populatedHtml], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const printWindow = window.open(url, '_blank');

        if (!printWindow) {
            throw new Error('PDF popup blocked');
        }

        // Revoke blob URL after enough time for the content to load and print
        setTimeout(() => URL.revokeObjectURL(url), 60000);

    } catch (error) {
        console.error('Error generating Internal PDF:', error);
        throw error;
    }
};

/**
 * Generate Order PDF (vendor order format with dynamic options)
 * Matches Order.js page logic exactly
 * 
 * @param {Object} measurement - The measurement data
 * @param {Function} t - Translation function from i18n
 * @returns {Promise<void>}
 */
export const generateOrderPdf = async (measurement, t) => {
    try {
        const response = await fetch('/measurementsTemplate.html');
        if (!response.ok) throw new Error('Template not found');
        const htmlText = await response.text();

        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlText, 'text/html');

        // Translate all elements with data-i18n attributes
        doc.querySelectorAll('[data-i18n]').forEach(el => {
            const key = el.getAttribute('data-i18n');
            el.textContent = t(key);
        });

        // Prepend patient name to the h1 title
        const titleEl = doc.querySelector('h1[data-i18n="pdfTemplate.title"]');
        const patientDisplayName = formatPatientNameForDisplay(
            measurement.patientFirstName,
            measurement.patientLastName,
            measurement.patientName
        );
        if (titleEl && patientDisplayName) {
            titleEl.textContent = `${patientDisplayName} - ${titleEl.textContent}`;
        }

        // Hide the Patient section in the PDF (as per Order.js logic)
        // The Patient section is the first section (index 0)
        const sections = doc.querySelectorAll('.section');
        if (sections.length >= 1) {
            const patientSection = sections[0];
            if (patientSection) {
                patientSection.style.display = 'none';
            }
        }

        // Check if measurements should be shown (only for Made-to-Measure)
        const isMadeToMeasure = measurement.measurementType === 'madeToMeasure';

        // Populate patient info (always needed for title)
        populatePatientInfo(doc, measurement, t);

        // Only show prescription details and measurements for Made-to-Measure orders
        if (isMadeToMeasure) {
            populatePrescriptionDetails(doc, measurement);
            populateHmMeasurements(doc, measurement);
            populateZmMeasurements(doc, measurement);
        } else {
            // Hide Prescription Details section for Off-the-Shelf orders
            const prescriptionSection = doc.querySelector('[data-i18n="pdfTemplate.sections.patientPrescription"]');
            if (prescriptionSection) {
                const section = prescriptionSection.closest('.section');
                if (section) {
                    section.style.display = 'none';
                }
            }

            // Hide Measurements section for Off-the-Shelf orders
            const measurementsSection = doc.querySelector('[data-i18n="pdfTemplate.sections.measurements"]');
            if (measurementsSection) {
                const section = measurementsSection.closest('.section');
                if (section) {
                    section.style.display = 'none';
                }
            }
        }

        // Use dynamic product options (same as Order.js)
        populateDynamicProductOptions(doc, measurement, t);

        const knittingInput = doc.getElementById('knitting');
        if (knittingInput) {
            const knittingGroup = knittingInput.closest('.form-group');
            if (knittingGroup) knittingGroup.style.display = 'none';
        }

        // Populate order-specific fields
        populateOrderFields(doc, measurement, t);

        // Hide the print button (not needed when auto-printing)
        const printBtn = doc.querySelector('.print-button');
        if (printBtn) printBtn.style.display = 'none';

        // Auto-trigger print dialog on load
        const script = doc.createElement('script');
        script.textContent = `window.addEventListener('load', function() { setTimeout(function() { window.print(); }, 400); });`;
        doc.body.appendChild(script);

        // Set document title
        doc.title = buildPdfFilename(
            formatPatientNameForFilename(measurement.patientFirstName, measurement.patientLastName),
            measurement.createdAt
        );

        // Serialize and create blob
        const serializer = new XMLSerializer();
        const populatedHtml = '<!DOCTYPE html>\n' + serializer.serializeToString(doc.documentElement);

        const blob = new Blob([populatedHtml], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const printWindow = window.open(url, '_blank');

        if (!printWindow) {
            throw new Error('PDF popup blocked');
        }

        // Revoke blob URL after enough time for the content to load and print
        setTimeout(() => URL.revokeObjectURL(url), 60000);

    } catch (error) {
        console.error('Error generating Order PDF:', error);
        throw error;
    }
};
